/**
 * AddOn Entity Schemas
 *
 * Mirrors the Prisma `AddOn` + `AddOnFeature` models
 * (apps/backend/prisma/schema/base.prisma). An AddOn is its own catalog
 * entity (KTD-8) that grants features via a feature-link join, attached to a
 * Subscription through `SubscriptionAddOn`. Parallel to `Plan`.
 *
 * `publicId` is exposed as `id`; raw BigInt internal ids are never surfaced.
 */

import { z } from 'zod'

/**
 * An add-on's grant of a single feature, referencing a `Feature` (KTD-4).
 */
export const AddOnFeatureSchema = z.object({
  id: z.string().describe('Add-on-feature public ID (cuid)'),
  featureId: z.string().describe('Granted Feature public ID'),
  featureSlug: z.string().nullable().describe('Granted Feature slug (feature identifier)'),
  isEnabled: z.boolean().describe('Whether the feature is granted by this add-on'),
  config: z.record(z.string(), z.unknown()).nullable().describe('Optional per-add-on config override for the feature'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type AddOnFeature = z.infer<typeof AddOnFeatureSchema>

export const AddOnSchema = z.object({
  id: z.string().describe('Add-on public ID (cuid)'),
  slug: z.string().describe('Stable add-on identifier'),
  name: z.string().describe('Add-on display name'),
  description: z.string().nullable().describe('Add-on description'),
  productId: z
    .string()
    .nullable()
    .describe('Owning Product public ID this add-on is scoped to; null = universal (attachable to any subscription)'),
  isActive: z.boolean().describe('Whether the add-on is offered'),
  features: z.array(AddOnFeatureSchema).describe('Feature grants for this add-on'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type AddOn = z.infer<typeof AddOnSchema>

/**
 * Minimal add-on reference for nesting inside subscriptions / list views.
 */
export const AddOnSummarySchema = AddOnSchema.pick({
  id: true,
  slug: true,
  name: true
})

export type AddOnSummary = z.infer<typeof AddOnSummarySchema>

/**
 * An add-on attached to a subscription (mirrors `SubscriptionAddOn`).
 */
export const SubscriptionAddOnSchema = z.object({
  id: z.string().describe('Subscription-add-on join public ID (cuid)'),
  addOn: AddOnSummarySchema.describe('The attached add-on'),
  isActive: z.boolean().describe('Whether the attachment is active'),
  createdAt: z.string().describe('ISO timestamp'),
  updatedAt: z.string().nullable().describe('ISO timestamp')
})

export type SubscriptionAddOn = z.infer<typeof SubscriptionAddOnSchema>
